import { useState } from 'react';
import { PrivacySettings, PrivacySection } from '@/hooks/usePrivacySettings';

interface PrivacyEditorProps {
  settings: PrivacySettings;
  onSave: (s: PrivacySettings) => void;
}

export default function PrivacyEditor({ settings, onSave }: PrivacyEditorProps) {
  const [form, setForm] = useState<PrivacySettings>(JSON.parse(JSON.stringify(settings)));
  const [saved, setSaved] = useState(false);

  const update = (field: keyof Omit<PrivacySettings, 'sections'>, value: string) => {
    setForm(p => ({ ...p, [field]: value }));
    setSaved(false);
  };

  const updateSection = (i: number, field: keyof PrivacySection, value: string) => {
    setForm(p => {
      const sections = [...p.sections];
      sections[i] = { ...sections[i], [field]: value };
      return { ...p, sections };
    });
    setSaved(false);
  };

  const addSection = () => {
    setForm(p => ({
      ...p,
      sections: [...p.sections, { title: '', content: '' }],
    }));
  };

  const removeSection = (i: number) => {
    setForm(p => ({
      ...p,
      sections: p.sections.filter((_, idx) => idx !== i),
    }));
  };

  const handleSave = () => {
    onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
            Editar Página Privacidade
          </h2>
          <p className="text-foreground-500 text-sm mt-1">
            Gerencie o conteúdo da política de privacidade do site.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setForm(JSON.parse(JSON.stringify(settings)))}
            className="px-4 py-2 text-sm text-foreground-600 border border-background-300 rounded-md hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            Descartar
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-2 text-sm font-bold rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 ${
              saved ? 'bg-green-500 text-white' : 'bg-primary-500 hover:bg-primary-600 text-white'
            }`}
          >
            {saved ? (
              <><i className="ri-checkbox-circle-line"></i> Salvo!</>
            ) : (
              <><i className="ri-save-line"></i> Salvar alterações</>
            )}
          </button>
        </div>
      </div>

      {/* Page header fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
            Título da Página
          </label>
          <input
            type="text"
            value={form.pageTitle}
            onChange={e => update('pageTitle', e.target.value)}
            className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white font-heading font-bold"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
            Subtítulo
          </label>
          <input
            type="text"
            value={form.pageSubtitle}
            onChange={e => update('pageSubtitle', e.target.value)}
            className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
          />
        </div>
        <div>
          <label className="block text-xs font-bold text-foreground-600 uppercase tracking-wide mb-1.5">
            Data da Última Atualização
          </label>
          <input
            type="text"
            value={form.lastUpdated}
            onChange={e => update('lastUpdated', e.target.value)}
            placeholder="Ex: 12 de junho de 2026"
            className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-primary-400 bg-white"
          />
        </div>
      </div>

      {/* Sections */}
      <div className="border-t border-background-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-bold text-foreground-500 uppercase tracking-wide">Seções</h3>
          <span className="text-xs text-foreground-400">{form.sections.length} seções</span>
        </div>

        <div className="space-y-4">
          {form.sections.map((section, i) => (
            <div key={i} className="border border-background-200 rounded-xl p-5 bg-background-50 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-foreground-500 uppercase tracking-wide">
                  Seção {i + 1}
                </span>
                {form.sections.length > 1 && (
                  <button
                    onClick={() => removeSection(i)}
                    className="w-7 h-7 flex items-center justify-center text-foreground-400 hover:text-red-500 cursor-pointer transition-colors"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )}
              </div>

              <div>
                <label className="block text-xs text-foreground-500 mb-1">Título da Seção</label>
                <input
                  type="text"
                  value={section.title}
                  onChange={e => updateSection(i, 'title', e.target.value)}
                  placeholder="Ex: 1. Informações que coletamos"
                  className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white font-bold"
                />
              </div>

              <div>
                <label className="block text-xs text-foreground-500 mb-1">Conteúdo</label>
                <textarea
                  value={section.content}
                  onChange={e => updateSection(i, 'content', e.target.value)}
                  rows={4}
                  maxLength={1500}
                  className="w-full border border-background-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary-400 bg-white resize-none"
                />
                <p className="text-xs text-foreground-400 text-right mt-0.5">{section.content.length}/1500</p>
              </div>
            </div>
          ))}

          {form.sections.length < 15 && (
            <button
              onClick={addSection}
              className="flex items-center gap-2 text-sm text-primary-500 hover:text-primary-700 cursor-pointer font-bold border-2 border-dashed border-primary-200 rounded-xl px-4 py-3 w-full justify-center hover:border-primary-400 transition-colors"
            >
              <i className="ri-add-line"></i> Adicionar seção
            </button>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="border-t border-background-200 pt-6">
        <h3 className="text-xs font-bold text-foreground-500 uppercase tracking-wide mb-4">Pré-visualização</h3>
        <div className="bg-background-100 rounded-xl p-6 border border-background-200 max-h-96 overflow-y-auto">
          <p className="font-heading font-bold text-foreground-950 text-xl mb-1">{form.pageTitle || '—'}</p>
          <p className="text-foreground-400 text-xs mb-6">{form.pageSubtitle || '—'}</p>
          <div className="space-y-5">
            {form.sections.map((s, i) => (
              <div key={i}>
                <h4 className="font-bold text-foreground-950 text-sm mb-2">{s.title || `—`}</h4>
                <p className="text-foreground-600 text-xs leading-relaxed">{s.content || '—'}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}